import { TFunction } from 'i18next';

import { NextAction, NextActionState, NextActionStopReason } from '@/shared/types/campaigns';
import { formatDateInTimezone, getUserTimezone } from '@/util/dateUtils';

const DATE_TIME_FORMAT = 'MMM d, HH:mm';
const DATE_FORMAT = 'MMM d';

/** Formats in the contact's time zone (with its abbreviation) when it is valid, else in the viewer's. */
export const formatNextActionTime = (
  ms: number,
  timeZone?: string,
  pattern: string = DATE_TIME_FORMAT
): string => {
  const date = new Date(ms);
  const inContactZone = timeZone ? formatDateInTimezone(date, timeZone, `${pattern} zzz`) : '';
  return inContactZone || formatDateInTimezone(date, getUserTimezone(), pattern);
};

export const NEXT_ACTION_DOT_COLOR: Record<NextActionState, string> = {
  not_started: 'bg-muted-foreground',
  deciding: 'bg-ai',
  in_conversation: 'bg-success',
  scheduled: 'bg-foreground',
  retrying: 'bg-warning',
  nothing_scheduled: 'bg-warning',
  on_hold: 'bg-muted-foreground',
  stopped: 'bg-destructive',
  finished: 'bg-success',
  managed_by_workflow: 'bg-muted-foreground',
};

const STOP_REASON_KEY: Record<NextActionStopReason, string> = {
  manual: 'campaigns.subscriber_journey.next_action.stop_reason.manual',
  contact_deleted: 'campaigns.subscriber_journey.next_action.stop_reason.contact_deleted',
  consent_revoked: 'campaigns.subscriber_journey.next_action.stop_reason.consent_revoked',
  goal_achieved: 'campaigns.subscriber_journey.next_action.stop_reason.goal_achieved',
  opt_out: 'campaigns.subscriber_journey.next_action.stop_reason.opt_out',
  compliance: 'campaigns.subscriber_journey.next_action.stop_reason.compliance',
  campaign_ended: 'campaigns.subscriber_journey.next_action.stop_reason.campaign_ended',
  first_message_failed: 'campaigns.subscriber_journey.next_action.stop_reason.first_message_failed',
  nba_stop: 'campaigns.subscriber_journey.next_action.stop_reason.nba_stop',
};

export const getStopReasonLabel = (t: TFunction, stopReason?: NextActionStopReason): string => {
  const key = stopReason ? STOP_REASON_KEY[stopReason] : undefined;
  return key ? t(key) : t('status.stopped');
};

const SHORT_LABEL_KEY: Record<
  Exclude<NextActionState, 'scheduled' | 'not_started' | 'stopped'>,
  string
> = {
  deciding: 'campaigns.subscriber_journey.next_action.short.deciding',
  in_conversation: 'campaigns.subscriber_journey.next_action.short.in_conversation',
  retrying: 'campaigns.subscriber_journey.next_action.short.retrying',
  nothing_scheduled: 'campaigns.subscriber_journey.next_action.short.nothing_scheduled',
  on_hold: 'campaigns.subscriber_journey.next_action.short.on_hold',
  finished: 'campaigns.subscriber_journey.next_action.short.finished',
  managed_by_workflow: 'campaigns.subscriber_journey.next_action.short.managed_by_workflow',
};

/** The one-line label shared by the logs table chip and the contact campaign card. */
export const getNextActionShortLabel = (
  t: TFunction,
  nextAction: NextAction,
  timeZone?: string
): string => {
  const { state, due_at } = nextAction;
  if (state === 'scheduled') {
    return due_at
      ? t('campaigns.subscriber_journey.next_action.short.scheduled', {
          0: formatNextActionTime(due_at, timeZone),
        })
      : t('campaigns.subscriber_journey.next_action.short.scheduled_no_date');
  }
  if (state === 'not_started') {
    return due_at
      ? t('campaigns.subscriber_journey.next_action.short.not_started', {
          0: formatNextActionTime(due_at, timeZone, DATE_FORMAT),
        })
      : t('campaigns.subscriber_journey.next_action.short.not_started_no_date');
  }
  if (state === 'stopped') {
    return getStopReasonLabel(t, nextAction.stop_reason);
  }
  return t(SHORT_LABEL_KEY[state]);
};
